import {
  Component,
  h,
  State,
  Listen,
  Element,
  Prop,
  Method,
  Event,
  EventEmitter,
  Host,
  Watch,
} from '@stencil/core';
import {
  Autocomplete,
  AutocompleteConfig,
  AutocompleteItem,
  isBreakpointSmaller,
} from '../../../utils';

@Component({
  tag: 'dc-ui-header-search',
  styleUrl: 'dc-ui-header-search.scss',
  shadow: true,
})
export class DcUiHeaderSearch {
  @Element() el: HTMLElement;
  @Prop() placeholder?: string;
  @Prop() value?: string;
  @Prop() apiUrl?: string;
  @Prop() formAction?: string;
  @Prop() formTarget?: '_blank' | '_self' | '_parent' | '_top';
  @Prop() autocompleteTermPlaceholder: string = '{query}';
  @Prop() searchQueryKey: string = 'q';
  @Prop() searchTriggerLabel: string = 'Search';
  @Prop() maxResults?: number = 10;
  @Prop() highlightResults: boolean = true;
  @Prop() autocompleteMinChars: number = 3;
  @Prop() autocompleteDebounceTime: number = 400;

  @Event() dcSearchSubmit: EventEmitter<{ value: string }>;
  @Event() dcSearchSelect: EventEmitter<{ item: AutocompleteItem }>;

  @State() isActive: boolean = false;
  @State() isSmallViewport: boolean = isBreakpointSmaller('lg');
  @State() inputValue: string = '';

  private formRef?: HTMLFormElement;
  private inputRef?: HTMLInputElement;
  private autocomplete?: Autocomplete;
  private timeoutId?: NodeJS.Timeout;

  componentWillLoad() {
    this.inputValue = this.value || '';
  }

  @Method()
  async getSearchValue(): Promise<string> {
    return this.inputRef?.value || '';
  }

  @Method()
  async clearSearchValue() {
    if (this.inputRef) {
      this.inputRef.value = '';
    }
  }

  @Method()
  async setSearchValue(value: string) {
    if (this.inputRef) {
      this.inputRef.value = value;
      this.inputValue = value;
    }
  }

  @Method()
  async toggleSearch() {
    this.updateSearchDimensions();
    this.isActive = !this.isActive;

    // Add focus handling when opening the search
    if (this.isActive) {
      this.timeoutId = setTimeout(() => {
        this.inputRef?.focus();
      }, 0);
    }
  }

  @Listen('focusout')
  handleFocusOut(event: FocusEvent) {
    if (
      !event.relatedTarget ||
      !(event.relatedTarget as HTMLElement).closest('dc-ui-header-search')
    ) {
      this.isActive = false;
    }
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.handleClose(event);
    }
  }

  @Listen('dcHeaderHidden', { target: 'window' })
  handleHeaderHidden() {
    this.closeSearch();
  }

  private updateSearchDimensions = () => {
    const parent = this.el.closest('dc-ui-header-base');
    if (parent) {
      parent.returnSearchExpandSize().then((size) => {
        this.el.style.setProperty('--search-expand-size', `${size}px`);
      });
    }
    this.isSmallViewport = isBreakpointSmaller('lg');
  };

  private handleFocus = () => {
    this.updateSearchDimensions();
    this.isActive = true;
  };

  private handleClear = () => {
    if (this.inputRef) {
      this.inputValue = '';
      this.inputRef.value = '';
    }
    this.inputRef?.blur();
    this.isActive = false;
  };

  private handleClose = (event: Event) => {
    event.preventDefault();
    this.closeSearch();
  };

  private handleToggleClick = () => {
    this.handleFocus();
    setTimeout(() => {
      this.inputRef?.focus();
    }, 0);
  };

  private handleInputChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    this.inputValue = input.value;
  };

  componentDidLoad() {
    window.addEventListener('resize', this.updateSearchDimensions);
    this.createHiddenInputs();
    if (this.apiUrl && this.inputRef) {
      const config: AutocompleteConfig = {
        url: this.apiUrl,
        searchTermPlaceholder: this.autocompleteTermPlaceholder,
        minChars: this.autocompleteMinChars,
        selector: '.search-input-container',
        resultListClass: 'search-suggestions-list',
        closeOnSelect: true,
        maxResults: this.maxResults,
        highlightResults: this.highlightResults,
        debounceTime: this.autocompleteDebounceTime,
        onSelect: (selectedItem) => {
          if (this.inputRef) {
            this.inputValue = selectedItem.name;
            this.inputRef.value = selectedItem.name;
            this.dcSearchSelect.emit({ item: selectedItem });
            setTimeout(() => {
              this.formRef?.submit();
            }, 0);
          }
        },
      };

      this.autocomplete = new Autocomplete(config, this.el, this.inputRef);
      this.autocomplete.init();
    }
  }

  private createHiddenInputs = () => {
    // check the formaction for properties after the ? and create hidden inputs for each property
    if (this.formAction) {
      const properties = this.formAction.split('?')[1].split('&');
      properties.forEach((property) => {
        const [key, value] = property.split('=');
        const hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = key;
        hiddenInput.value = value;
        this.formRef?.appendChild(hiddenInput);
      });
    }
  };

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateSearchDimensions);
    this.autocomplete?.destroy();
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  private handleSubmit = (event: Event) => {
    event.preventDefault();

    if (!this.formAction || !this.inputValue || !this.inputRef) {
      return;
    }

    this.inputRef.value = this.inputValue;

    this.formRef?.submit();
  };

  @Watch('apiUrl')
  @Watch('autocompleteTermPlaceholder')
  @Watch('maxResults')
  @Watch('highlightResults')
  handleAutocompleteConfigChange() {
    // Destroy existing instance
    this.autocomplete?.destroy();

    // Only reinitialize if we have required props
    if (this.apiUrl && this.inputRef) {
      const config: AutocompleteConfig = {
        url: this.apiUrl,
        searchTermPlaceholder: this.autocompleteTermPlaceholder,
        minChars: 1,
        selector: '.search-input-container',
        resultListClass: 'search-suggestions-list',
        closeOnSelect: true,
        maxResults: this.maxResults,
        highlightResults: this.highlightResults,
        onSelect: (selectedItem) => {
          if (this.inputRef) {
            this.inputValue = selectedItem.name;
            this.inputRef.value = selectedItem.name;
            this.dcSearchSelect.emit({ item: selectedItem });
            setTimeout(() => {
              this.formRef?.submit();
            }, 0);
          }
        },
      };

      this.autocomplete = new Autocomplete(config, this.el, this.inputRef);
      this.autocomplete.init();
    }
  }

  render() {
    const shouldDisableTabbing = !this.isActive && this.isSmallViewport;

    return (
      <Host>
        <button
          type="button"
          class={{ 'search-toggle': true, 'is-active': this.isActive }}
          onClick={this.handleToggleClick}>
          <dc-ui-icon
            name="dc_search"
            size="medium"
            color="darkBlue02"
            tabindex={shouldDisableTabbing ? -1 : null}></dc-ui-icon>
        </button>

        <form
          action={this.formAction || ''}
          target={this.formTarget || ''}
          method="GET"
          ref={(el) => (this.formRef = el)}
          onSubmit={this.handleSubmit}
          class={{ 'search-container': true, 'is-active': this.isActive }}>
          <div class="search-input-container">
            <button
              aria-label={this.searchTriggerLabel}
              type="submit"
              class="search-icon"
              tabindex={shouldDisableTabbing || !this.inputValue ? -1 : null}>
              <dc-ui-icon
                name="dc_search"
                color="darkBlue02"
                size="medium"></dc-ui-icon>
            </button>
            <input
              type="search"
              ref={(el) => (this.inputRef = el)}
              onFocus={this.handleFocus}
              onInput={this.handleInputChange}
              placeholder={this.placeholder}
              value={this.inputValue}
              tabindex={shouldDisableTabbing ? -1 : null}
              name={this.searchQueryKey}
            />
            {this.isActive && (
              <button
                type="button"
                class="close-button"
                onClick={this.handleClear}>
                <dc-ui-icon
                  name="dc_close"
                  size="medium"></dc-ui-icon>
              </button>
            )}
          </div>
        </form>
      </Host>
    );
  }

  private closeSearch = () => {
    this.isActive = false;
    this.inputRef?.blur();
  };
}
